/**
 * Classifies environment failure messages into structured error info.
 *
 * This is a frontend bridge: it pattern-matches on `failureMessage` strings from
 * environment status and returns a classified error with code, message, and metadata.
 * When the backend starts attaching structured errors to Connect responses,
 * this classifier becomes the fallback for environments without structured errors.
 *
 * Actions (UI buttons) are intentionally separate from the error classification.
 * The classifier determines *what* went wrong; the ERROR_ACTIONS mapping determines
 * *how* the frontend lets the user respond. This keeps the error shape backend-compatible
 * and the action mapping a pure frontend concern.
 */

export type ErrorActionTarget = { type: "dashboard_path"; path: string } | { type: "external_url"; url: string };

export type ErrorActionInfo = {
    actionId: string;
    label: string;
    target: ErrorActionTarget;
};

/** Pure error classification — no UI actions. */
export type ActionableError = {
    errorCode: string;
    userMessage: string;
    metadata: Record<string, string>;
};

/** Optional context passed to the classifier to enrich error messages. */
export type ClassifierContext = {
    /** The repository URL, shown in error messages so users know which repo failed. */
    repoUrl?: string;
};

type ErrorPattern = {
    /** Substring(s) to match against the failure message (case-insensitive). Any match triggers. */
    patterns: string[];
    /** The classified error to return when matched. */
    error: ActionableError;
};

const GIT_AUTH_SETTINGS_ACTION: ErrorActionInfo = {
    actionId: "configure_git_auth",
    label: "Configure Git Authentication",
    target: { type: "dashboard_path", path: "/settings/git-authentication" },
};

/**
 * Maps error codes to frontend actions. This is a UI concern — the backend
 * should not dictate what buttons to show. The classifier returns the error
 * classification; consumers look up actions here by error code.
 */
export const ERROR_ACTIONS: Record<string, ErrorActionInfo[]> = {
    CONTENT_GIT_ACCESS_DENIED: [GIT_AUTH_SETTINGS_ACTION],
    CONTENT_GIT_AUTH_FAILED: [GIT_AUTH_SETTINGS_ACTION],
    CONTENT_GIT_HTTP_AUTH_DENIED: [GIT_AUTH_SETTINGS_ACTION],
    CONTENT_GIT_INVALID_TOKEN: [GIT_AUTH_SETTINGS_ACTION],
    SCM_RATE_LIMITED: [],
};

/**
 * Content/git error patterns, ordered from most specific to least specific.
 * Order matters: the first match wins.
 */
const CONTENT_ERROR_PATTERNS: ErrorPattern[] = [
    {
        patterns: ["Cannot find valid SSO", "SSO session"],
        error: {
            errorCode: "CONTENT_GIT_SSO_EXPIRED",
            userMessage: "Your SSO session has expired. Re-authenticate with your identity provider.",
            metadata: {},
        },
    },
    {
        patterns: ["HTTP Basic: Access denied"],
        error: {
            errorCode: "CONTENT_GIT_HTTP_AUTH_DENIED",
            userMessage: "Git HTTP authentication denied. Check your credentials or re-authenticate.",
            metadata: {},
        },
    },
    {
        patterns: ["Repository not found"],
        error: {
            errorCode: "CONTENT_GIT_REPO_NOT_FOUND",
            userMessage: "Repository not found. Check the URL and your access permissions.",
            metadata: {},
        },
    },
    {
        patterns: ["Invalid username"],
        error: {
            errorCode: "CONTENT_GIT_INVALID_TOKEN",
            userMessage: "Git token is invalid or expired. Re-authenticate to refresh your credentials.",
            metadata: {},
        },
    },
    {
        patterns: ["rate limit", "rate_limit"],
        error: {
            errorCode: "SCM_RATE_LIMITED",
            userMessage: "SCM rate limit exceeded. Wait a moment and try again.",
            metadata: {},
        },
    },
    {
        patterns: ["CLONE_TARGET_MODE_UNSPECIFIED"],
        error: {
            errorCode: "CONTENT_INVALID_CLONE_TARGET",
            userMessage: "Invalid clone target. Check your project configuration.",
            metadata: {},
        },
    },
    {
        patterns: ["authentication failed", "not authenticated"],
        error: {
            errorCode: "CONTENT_GIT_AUTH_FAILED",
            userMessage: "Git authentication failed. Your credentials may have expired.",
            metadata: {},
        },
    },
    {
        // Most generic git access pattern — must be last among git auth errors
        patterns: ["access denied"],
        error: {
            errorCode: "CONTENT_GIT_ACCESS_DENIED",
            userMessage: "Git access denied. Connect your account to access this repository.",
            metadata: {},
        },
    },
];

/**
 * Machine/infrastructure error patterns, ordered from most specific to least specific.
 */
const MACHINE_ERROR_PATTERNS: ErrorPattern[] = [
    {
        patterns: ["ssm:SendCommand", "SSM"],
        error: {
            errorCode: "MACHINE_SSM_ACCESS_DENIED",
            userMessage:
                "Your environment could not start due to a missing AWS permission. Ask your administrator to check the runner setup.",
            metadata: {},
        },
    },
    {
        patterns: ["InsufficientFreeAddresses", "subnet"],
        error: {
            errorCode: "MACHINE_SUBNET_EXHAUSTED",
            userMessage:
                "Your environment could not start because the network is full. Ask your administrator to free up resources or expand capacity.",
            metadata: {},
        },
    },
    {
        patterns: ["terminated externally"],
        error: {
            errorCode: "MACHINE_VM_TERMINATED",
            userMessage:
                "This environment was shut down externally and cannot be restarted. Please delete it and create a new one.",
            metadata: {},
        },
    },
    {
        patterns: ["could not reach the environment", "Local Network permission"],
        error: {
            errorCode: "MACHINE_LOCAL_NETWORK",
            userMessage:
                "Could not connect to the environment. Grant the Local Network permission in macOS System Preferences (Privacy & Security > Local Network).",
            metadata: {},
        },
    },
    {
        patterns: ["agent has been inactive", "agent inactive"],
        error: {
            errorCode: "MACHINE_AGENT_TIMEOUT",
            userMessage:
                "The environment became unresponsive and was shut down. Try creating a new environment.",
            metadata: {},
        },
    },
    {
        patterns: ["could not be prepared for snapshotting"],
        error: {
            errorCode: "MACHINE_SNAPSHOT_FAILED",
            userMessage:
                "The prebuild could not be saved in time. Retry the prebuild, or contact support if this keeps happening.",
            metadata: {},
        },
    },
    {
        patterns: ["failed to send the initial configuration"],
        error: {
            errorCode: "MACHINE_CONFIG_DELIVERY_FAILED",
            userMessage:
                "Your environment could not be configured. This is usually temporary. If it persists, ask your administrator to check the runner logs.",
            metadata: {},
        },
    },
    {
        patterns: ["out of capacity"],
        error: {
            errorCode: "MACHINE_OUT_OF_CAPACITY",
            userMessage:
                "No capacity available to start your environment. Try again in a few minutes, or ask your administrator to add resources.",
            metadata: {},
        },
    },
    {
        patterns: ["exited unexpectedly"],
        error: {
            errorCode: "MACHINE_UNEXPECTED_EXIT",
            userMessage: "The environment stopped unexpectedly. Try restarting or creating a new one.",
            metadata: {},
        },
    },
    {
        // Most generic machine pattern — must be last
        patterns: ["system exception"],
        error: {
            errorCode: "MACHINE_SYSTEM_EXCEPTION",
            userMessage: "Something went wrong while starting your environment. Try creating a new one, or contact support if this keeps happening.",
            metadata: {},
        },
    },
];

const AUTOMATIONS_ERROR_PATTERNS: ErrorPattern[] = [
    {
        patterns: ["not found", "no such file"],
        error: {
            errorCode: "AUTOMATIONS_FILE_NOT_FOUND",
            userMessage: "Automations file not found. Check that the file exists in your repository.",
            metadata: {},
        },
    },
    {
        patterns: ["syntax", "parse error", "unmarshal", "cannot read"],
        error: {
            errorCode: "AUTOMATIONS_FILE_PARSE_ERROR",
            userMessage: "Automations file has a syntax error. Check the file format.",
            metadata: {},
        },
    },
    {
        patterns: ["permission denied", "403"],
        error: {
            errorCode: "AUTOMATIONS_FILE_PERMISSION_DENIED",
            userMessage: "Permission denied reading automations file.",
            metadata: {},
        },
    },
];

/**
 * DevContainer error patterns, ordered from most specific to least specific.
 */
const DEVCONTAINER_ERROR_PATTERNS: ErrorPattern[] = [
    {
        patterns: ["Creating recovery container"],
        error: {
            errorCode: "DEVCONTAINER_RECOVERY_FAILED",
            userMessage:
                "Recovery container creation failed. The environment may not be accessible. Try creating a new environment.",
            metadata: {},
        },
    },
    {
        patterns: ["DNS", "name resolution"],
        error: {
            errorCode: "DEVCONTAINER_DNS_FAILED",
            userMessage:
                "DNS resolution failed. If you're on a restricted network, check that your runner can reach the required registries.",
            metadata: {},
        },
    },
    {
        patterns: ["pull access denied", "manifest unknown", "unauthorized"],
        error: {
            errorCode: "DEVCONTAINER_REGISTRY_ERROR",
            userMessage:
                "Container image pull failed. Check that the image exists and your runner has access to the registry.",
            metadata: {},
        },
    },
    {
        patterns: ["Dockerfile", "dockerfile parse"],
        error: {
            errorCode: "DEVCONTAINER_DOCKERFILE_ERROR",
            userMessage: "Dockerfile syntax error. Check your Dockerfile for errors.",
            metadata: {},
        },
    },
    {
        patterns: ["Building dev container"],
        error: {
            errorCode: "DEVCONTAINER_BUILD_FAILED",
            userMessage:
                "Dev container build failed. Check the build logs for details. You can rebuild after fixing the issue.",
            metadata: {},
        },
    },
    {
        // Generic devcontainer creation failure — must be last
        patterns: ["Creating dev container", "cannot run devcontainer up"],
        error: {
            errorCode: "DEVCONTAINER_CREATE_FAILED",
            userMessage: "Dev container creation failed. Check the logs for details.",
            metadata: {},
        },
    },
];

const PATTERNS_BY_COMPONENT: Record<string, ErrorPattern[]> = {
    content: CONTENT_ERROR_PATTERNS,
    machine: MACHINE_ERROR_PATTERNS,
    automations: AUTOMATIONS_ERROR_PATTERNS,
    devcontainer: DEVCONTAINER_ERROR_PATTERNS,
};

function matchPatterns(failureMessage: string, patterns: ErrorPattern[]): ActionableError | null {
    const lower = failureMessage.toLowerCase();
    for (const pattern of patterns) {
        if (pattern.patterns.some((p) => lower.includes(p.toLowerCase()))) {
            return pattern.error;
        }
    }
    return null;
}

/** Error codes where the repo URL is relevant context for the user message. */
const REPO_URL_ERROR_CODES = new Set([
    "CONTENT_GIT_ACCESS_DENIED",
    "CONTENT_GIT_AUTH_FAILED",
    "CONTENT_GIT_HTTP_AUTH_DENIED",
    "CONTENT_GIT_INVALID_TOKEN",
    "CONTENT_GIT_SSO_EXPIRED",
    "CONTENT_GIT_REPO_NOT_FOUND",
]);

/**
 * Enriches a classified error with context (e.g. repo URL).
 * Returns a new object — the pattern templates are not mutated.
 */
function applyContext(error: ActionableError, context?: ClassifierContext): ActionableError {
    if (!context?.repoUrl || !REPO_URL_ERROR_CODES.has(error.errorCode)) {
        return error;
    }

    return { ...error, metadata: { ...error.metadata, repoUrl: context.repoUrl } };
}

/**
 * Classifies an environment failure message into a structured error.
 *
 * @param component - The environment status component that failed
 * @param failureMessage - The raw failure message string
 * @param context - Optional context (e.g. repo URL) to enrich the error
 * @returns Classified error if the message matches a known pattern, null otherwise
 */
export function classifyEnvironmentError(
    component: "content" | "machine" | "automations" | "devcontainer" | "secrets",
    failureMessage: string | undefined,
    context?: ClassifierContext,
): ActionableError | null {
    if (!failureMessage) {
        return null;
    }

    const patterns = PATTERNS_BY_COMPONENT[component];
    if (!patterns) {
        return null;
    }

    const match = matchPatterns(failureMessage, patterns);
    if (!match) {
        return null;
    }

    return applyContext(match, context);
}
